import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { useEmployeeRecognitionUpdate } from "@/queries/employees/use-employee-recognitions.query.ts";
import { ApiEmployeeShort } from "@/types/employees/employee.types.ts";
import { ApiEmployeeRecognition } from "@/types/recognitions/recognition.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Modal, ModalProps, Stack, Textarea, Title, useModals } from "@skailer-com/uikit";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

export interface EmployeeRecognitionEditModalProps extends ModalProps {
  employee: ApiEmployeeShort;
  recognition: ApiEmployeeRecognition;
}

export function EmployeeRecognitionEditModal({
  employee,
  recognition,
  ...props
}: EmployeeRecognitionEditModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = props;

  const editSchema = z.object({
    comment: z.string(),
    datetime_awarded_at: z.date({ error: t("errors:field_required") }),
  });
  type EditSchema = z.infer<typeof editSchema>;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EditSchema>({
    mode: "onSubmit",
    resolver: zodResolver(editSchema),
    defaultValues: {
      comment: recognition.comment ?? "",
      datetime_awarded_at: recognition.datetime_awarded_at,
    },
  });

  const update = useEmployeeRecognitionUpdate(control);

  const onSubmit = handleSubmit(async (data) => {
    try {
      await update.mutateAsync({
        employeeId: employee.id,
        itemId: recognition.id,
        payload: {
          comment: data.comment.trim() || null,
          datetime_awarded_at: data.datetime_awarded_at,
        },
      });
      close(id);
    } catch {}
  });

  return (
    <Modal
      {...props}
      title={t("recognition:edit_title")}
      caption={recognition.recognition_title}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button type={"submit"} size={"lg"} isLoading={update.isPending} onClick={onSubmit}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Stack gap={"md"}>
          <Controller
            control={control}
            name={"datetime_awarded_at"}
            render={({ field: { value, onChange }, fieldState }) => (
              <Datepicker
                value={value}
                onChange={onChange}
                required={true}
                label={t("recognition:field_awarded_at")}
                error={fieldState.error?.message}
              />
            )}
          />
          <Stack gap={"xs"}>
            <Title size={"xs"} header={t("recognition:gift_comment_label")} />
            <Controller
              control={control}
              name={"comment"}
              render={({ field, fieldState }) => (
                <Textarea
                  label={t("recognition:gift_comment_placeholder")}
                  {...field}
                  error={fieldState.error?.message}
                />
              )}
            />
          </Stack>
        </Stack>
      </form>
    </Modal>
  );
}
